/*
 * ai-mark.js — machine-readable "AI-generated" marking for downloadable
 * outputs of generative tools (EU AI Act Art. 50(2): outputs of AI systems
 * generating synthetic audio/image/video must be marked in a machine-readable
 * format; existing systems must comply by 2026-12-02, tools launched after
 * 2026-08-02 from day one).
 *
 * Design constraints:
 *  - Marking runs at the DOWNLOAD boundary (last step before __downloadFile),
 *    never mid-pipeline — so concatenated/segment blobs are marked once.
 *  - Marking must NEVER break a download: every entry point resolves with the
 *    ORIGINAL blob on any error or on unknown container layout.
 *  - Idempotent: a blob that already carries our marker passes through.
 *  - A time-based file (MP3, WAV, MP4, WebM, Ogg) is never read whole: the
 *    mark reads the header it changes, and the result is Blob parts around
 *    it. Its length has no ceiling — a multi-GB export read whole is two
 *    copies of it at the last step of a run, and past 2 GiB the read fails
 *    and the file leaves unmarked. Images are bounded by the canvas and keep
 *    the plain whole-buffer writers.
 *
 * Formats:
 *  - MP3  → prepends an ID3v2.3 tag: TXXX "AI_GENERATED"="true",
 *           TXXX "digitalsourcetype"="trainedAlgorithmicMedia" (IPTC term),
 *           COMM (eng) human-readable note. Skipped if an ID3v2 tag exists.
 *  - WAV  → appends a LIST/INFO chunk (ICMT + ISFT) and fixes the RIFF size.
 *  - PNG  → inserts tEXt chunks (Comment + Software) right after IHDR.
 *  - JPEG → inserts APP1/XMP (IPTC DigitalSourceType) + a COM note after SOI.
 *  - MP4  → moov > udta > meta > ilst comment atoms (see that section).
 *  - WebM → a top-level Tags element in front of the first Cluster, where
 *           demuxers read tags; the Segment positions past it move with it.
 *  - WebP → rebuilt as VP8X + an XMP chunk (canvas writes the simple form).
 *  - Ogg  → AI_GENERATED/DIGITAL_SOURCE_TYPE/COMMENT into the Vorbis/Opus
 *           comment header; the carrying page is re-laced and its CRC redone.
 *
 * Usage: __aiMark.mp3(blob, 'text-to-speech').then(marked => ...)
 *        __aiMark.auto(blob, filename, tool) — dispatch by file extension.
 */
(function () {
    'use strict';

    var enc = new TextEncoder();

    function noteText(tool) {
        var host = (location && location.hostname) ? location.hostname : 'timbrica.com';
        return 'AI-generated content (tool: ' + (tool || 'generator') + ', ' + host + ')';
    }

    /* ---------------- ID3v2.3 (MP3) ---------------- */

    function syncsafe(n) {
        return [(n >> 21) & 0x7f, (n >> 14) & 0x7f, (n >> 7) & 0x7f, n & 0x7f];
    }

    function u32be(n) {
        return [(n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff];
    }

    // TXXX: enc(0) + description + 0x00 + value  (ASCII content only here)
    function txxxFrame(desc, value) {
        var d = enc.encode(desc), v = enc.encode(value);
        var body = new Uint8Array(1 + d.length + 1 + v.length);
        body.set(d, 1);
        body[1 + d.length] = 0;
        body.set(v, 2 + d.length);
        return frame('TXXX', body);
    }

    // COMM: enc(0) + lang 'eng' + short-desc 0x00 + text
    function commFrame(text) {
        var t = enc.encode(text);
        var body = new Uint8Array(1 + 3 + 1 + t.length);
        body[1] = 0x65; body[2] = 0x6e; body[3] = 0x67; // 'eng'
        body.set(t, 5);
        return frame('COMM', body);
    }

    function frame(id, body) {
        var out = new Uint8Array(10 + body.length);
        for (var i = 0; i < 4; i++) out[i] = id.charCodeAt(i);
        out.set(u32be(body.length), 4); // v2.3 frame size: plain 32-bit BE
        out.set(body, 10);
        return out;
    }

    function buildId3(tool) {
        var frames = [
            txxxFrame('AI_GENERATED', 'true'),
            txxxFrame('digitalsourcetype', 'trainedAlgorithmicMedia'),
            commFrame(noteText(tool)),
        ];
        var size = frames.reduce(function (s, f) { return s + f.length; }, 0);
        var tag = new Uint8Array(10 + size);
        tag[0] = 0x49; tag[1] = 0x44; tag[2] = 0x33; // 'ID3'
        tag[3] = 3; tag[4] = 0; tag[5] = 0;          // v2.3, no flags
        tag.set(syncsafe(size), 6);
        var off = 10;
        frames.forEach(function (f) { tag.set(f, off); off += f.length; });
        return tag;
    }

    function markMp3(blob, tool) {
        return blob.slice(0, 3).arrayBuffer().then(function (head) {
            var h = new Uint8Array(head);
            // Existing ID3v2 (e.g. endless-piano's writer) → leave untouched:
            // two v2 tags in one file confuse strict parsers.
            if (h.length >= 3 && h[0] === 0x49 && h[1] === 0x44 && h[2] === 0x33) return blob;
            return new Blob([buildId3(tool), blob], { type: blob.type || 'audio/mpeg' });
        });
    }

    /* ---------------- RIFF/WAVE LIST-INFO ---------------- */

    function infoSub(id, text) {
        var t = enc.encode(text);
        var pad = t.length % 2; // word-align
        var out = new Uint8Array(8 + t.length + pad);
        for (var i = 0; i < 4; i++) out[i] = id.charCodeAt(i);
        new DataView(out.buffer).setUint32(4, t.length, true);
        out.set(t, 8);
        return out;
    }

    /* The tag is appended, so only the 12-byte RIFF header is ever read: the result is
       [new header, the file from byte 8 on, LIST] as Blob parts. Reading the whole file and
       copying it once more put two full copies of a WAV in memory at the very end of a run —
       the stem mix of /music-generator, the class of Sentry TIMBRICA-FRONTEND-NA (30.09.2026). */
    function markWav(blob, tool) {
        if (blob.size < 44) return Promise.resolve(blob);
        return blob.slice(0, 12).arrayBuffer().then(function (buf) {
            var dv = new DataView(buf);
            // 'RIFF' ... 'WAVE'
            if (dv.getUint32(0, false) !== 0x52494646 || dv.getUint32(8, false) !== 0x57415645) return blob;

            var note = noteText(tool);
            var subs = [infoSub('ICMT', note), infoSub('ISFT', 'Timbrica AI tools')];
            var subsLen = subs.reduce(function (s, x) { return s + x.length; }, 0);
            var list = new Uint8Array(12 + subsLen);
            list[0] = 0x4c; list[1] = 0x49; list[2] = 0x53; list[3] = 0x54; // LIST
            new DataView(list.buffer).setUint32(4, 4 + subsLen, true);
            list[8] = 0x49; list[9] = 0x4e; list[10] = 0x46; list[11] = 0x4f; // INFO
            var off = 12;
            subs.forEach(function (s) { list.set(s, off); off += s.length; });

            var head = new Uint8Array(buf.slice(0, 8));
            // RIFF chunk size = total - 8
            new DataView(head.buffer).setUint32(4, blob.size + list.length - 8, true);
            return new Blob([head, blob.slice(8), list], { type: blob.type || 'audio/wav' });
        });
    }

    /* ---------------- PNG tEXt ---------------- */

    var CRC_TABLE = (function () {
        var t = new Int32Array(256);
        for (var n = 0; n < 256; n++) {
            var c = n;
            for (var k = 0; k < 8; k++) c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
            t[n] = c;
        }
        return t;
    })();

    function crc32(bytes) {
        var c = -1;
        for (var i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
        return (c ^ -1) >>> 0;
    }

    // keyword and text must be Latin-1 printable — callers pass ASCII only.
    function textChunk(keyword, text) {
        var k = enc.encode(keyword), t = enc.encode(text);
        var data = new Uint8Array(k.length + 1 + t.length);
        data.set(k, 0);
        data.set(t, k.length + 1);
        var out = new Uint8Array(12 + data.length);
        var dv = new DataView(out.buffer);
        dv.setUint32(0, data.length, false);
        out[4] = 0x74; out[5] = 0x45; out[6] = 0x58; out[7] = 0x74; // tEXt
        out.set(data, 8);
        var crcBuf = new Uint8Array(4 + data.length);
        crcBuf.set(out.subarray(4, 8), 0);
        crcBuf.set(data, 4);
        dv.setUint32(8 + data.length, crc32(crcBuf), false);
        return out;
    }

    function markPng(blob, tool) {
        return blob.arrayBuffer().then(function (buf) {
            var u8 = new Uint8Array(buf);
            if (u8.length < 33) return blob;
            var dv = new DataView(buf);
            // PNG signature
            if (dv.getUint32(0, false) !== 0x89504e47 || dv.getUint32(4, false) !== 0x0d0a1a0a) return blob;
            // First chunk must be IHDR; insertion point = right after it.
            var ihdrLen = dv.getUint32(8, false);
            if (dv.getUint32(12, false) !== 0x49484452) return blob; // 'IHDR'
            var insertAt = 8 + 12 + ihdrLen;
            if (insertAt >= u8.length) return blob;
            // Already marked: our tEXt chunks sit right after IHDR. A tool that
            // marks its PNG itself and then saves through __downloadFile
            // (/face-maker) used to get a second pair.
            if (containsAscii(u8, NOTE_PREFIX, 65536)) return blob;

            var chunks = [
                textChunk('Comment', noteText(tool)),
                textChunk('Software', 'Timbrica ' + (tool || 'generator') + ' (AI)'),
            ];
            var extra = chunks.reduce(function (s, c) { return s + c.length; }, 0);
            var out = new Uint8Array(u8.length + extra);
            out.set(u8.subarray(0, insertAt), 0);
            var off = insertAt;
            chunks.forEach(function (c) { out.set(c, off); off += c.length; });
            out.set(u8.subarray(insertAt), off);
            return new Blob([out], { type: blob.type || 'image/png' });
        });
    }

    /* ---------------- ISO-BMFF (MP4/M4A/MOV) udta/meta/ilst ---------------- */
    /*
     * Adds the iTunes comment atoms (moov > udta > meta > ilst > [©cmt ©too])
     * that ffprobe/exiftool read as "comment". The insertion into the udta is
     * udtaWithMark (below) — one owner for this marker and for writers that
     * build a moov from byte ranges (MP4TrackRemux.replaceAudio).
     *
     * Reads the top-level box HEADERS and the moov, never the file
     * (02.10.2026): the result is Blob parts [the file up to the moov, the new
     * moov, the file after it]. It used to read the export whole and copy it
     * once more, and from 2 GiB the read itself failed — a /slideshow-maker,
     * /video-to-sound or /video-dubbing result of that size left unmarked.
     *   - moov BEFORE the samples (mp4-muxer fastStart, -movflags faststart):
     *     the grown moov moves every sample stored after it, so each
     *     stco/co64 entry past the old moov gets +delta. An stco entry the
     *     move would push past 4 GiB leaves the file unmarked (it used to
     *     wrap silently and point that chunk at the wrong bytes).
     *   - moov last: nothing after it moves, nothing to patch.
     *   - 64-bit (largesize) boxes — an mdat past 4 GiB — are walked.
     * Refuses (returns the original blob): fragmented MP4 (moof/sidx carry
     * absolute offsets this does not patch), boxes that do not tile the file,
     * a malformed moov, and a file already carrying our note.
     */

    function box(type, payload) {
        var out = new Uint8Array(8 + payload.length);
        new DataView(out.buffer).setUint32(0, out.length, false);
        for (var i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i) & 0xff;
        out.set(payload, 8);
        return out;
    }

    function concatU8(parts) {
        var total = parts.reduce(function (s, p) { return s + p.length; }, 0);
        var out = new Uint8Array(total);
        var off = 0;
        parts.forEach(function (p) { out.set(p, off); off += p.length; });
        return out;
    }

    // iTunes metadata item: '©cmt' > 'data' (type 1 = UTF-8, 4-byte locale 0)
    function ilstItem(name4, text) {
        var t = enc.encode(text);
        var data = new Uint8Array(8 + t.length);
        new DataView(data.buffer).setUint32(0, 1, false);
        data.set(t, 8);
        var payload = box('data', data);
        var out = new Uint8Array(8 + payload.length);
        new DataView(out.buffer).setUint32(0, out.length, false);
        for (var i = 0; i < 4; i++) out[4 + i] = name4.charCodeAt(i) & 0xff;
        out.set(payload, 8);
        return out;
    }

    // hdlr FullBox for meta: ver/flags + pre_defined + 'mdir' + 'appl' + zeros + NUL
    function metaHdlr() {
        var h = new Uint8Array(25);
        h.set(enc.encode('mdir'), 8);
        h.set(enc.encode('appl'), 12);
        return h;
    }

    var MP4_CONTAINERS = { moov: 1, trak: 1, mdia: 1, minf: 1, stbl: 1 };

    // Where the moov is, from one header read per top-level box — or null for
    // a layout this marker leaves alone.
    function mp4Moov(blob) {
        var size = blob.size, moov = null, boxes = 0;
        function at(off) {
            if (off === size) return moov;
            if (off + 8 > size || ++boxes > 4096) return null;
            return bytesOf(blob, off, Math.min(size, off + 16)).then(function (h) {
                var dv = new DataView(h.buffer, h.byteOffset, h.byteLength);
                var len = dv.getUint32(0, false), head = 8, type = fourcc(h, 4);
                if (len === 1) {                            // 64-bit largesize
                    if (h.length < 16) return null;
                    len = dv.getUint32(8, false) * 4294967296 + dv.getUint32(12, false);
                    head = 16;
                } else if (len === 0) {
                    len = size - off;                       // "runs to the end of the file"
                }
                if (len < head || off + len > size) return null;
                if (off === 0 && type !== 'ftyp') return null;
                if (type === 'moof' || type === 'sidx' || type === 'styp') return null;
                if (type === 'moov') {
                    if (moov || head !== 8) return null;
                    moov = { start: off, end: off + len };
                }
                return at(off + len);
            });
        }
        return Promise.resolve(at(0));
    }

    // The moov `m` (found at file offset `base`) with our note in its udta, or
    // null: already marked, malformed, or a move no stco entry can hold.
    function mp4MarkedMoov(m, base, tool) {
        if (m.length < 8) return null;
        var dv = new DataView(m.buffer, m.byteOffset, m.byteLength);
        if (dv.getUint32(0, false) !== m.length || fourcc(m, 4) !== 'moov') return null;
        try {
            if (new TextDecoder('latin1').decode(m).indexOf(NOTE_PREFIX) !== -1) return null;
        } catch (e) { /* no latin1 decoder — udtaWithMark still checks its box */ }

        var tables = [], udtaAt = -1, udtaLen = 0, bad = false;
        (function scan(start, end, depth) {
            for (var p = start; p + 8 <= end;) {
                var s = dv.getUint32(p, false);
                if (s < 8 || p + s > end) { bad = true; return; }
                var t = fourcc(m, p + 4);
                if (t === 'stco' || t === 'co64') tables.push({ wide: t === 'co64', at: p, len: s });
                else if (t === 'udta' && depth === 0) { if (udtaAt < 0) { udtaAt = p; udtaLen = s; } }
                else if (MP4_CONTAINERS[t]) scan(p + 8, p + s, depth + 1);
                p += s;
            }
        })(8, m.length, 0);
        if (bad) return null;

        var old = udtaAt < 0 ? null : m.subarray(udtaAt, udtaAt + udtaLen);
        var udta = udtaWithMark(old, tool);
        if (udta === old) return null;
        var at = udtaAt < 0 ? m.length : udtaAt, cut = udtaAt < 0 ? 0 : udtaLen;
        var delta = udta.length - cut;
        var out = new Uint8Array(m.length + delta);
        out.set(m.subarray(0, at), 0);
        out.set(udta, at);
        out.set(m.subarray(at + cut), at + udta.length);
        var odv = new DataView(out.buffer);
        odv.setUint32(0, out.length, false);

        // Samples stored after the old moov moved by delta; those before it stay.
        var after = base + m.length;
        for (var i = 0; i < tables.length; i++) {
            var tb = tables[i], w = tb.wide ? 8 : 4;
            var p = tb.at > at ? tb.at + delta : tb.at;     // the table's place in the new moov
            if (tb.len < 16) return null;
            var n = odv.getUint32(p + 12, false);           // FullBox: size, type, ver/flags, count
            if (16 + n * w > tb.len) return null;
            for (var k = 0, e = p + 16; k < n; k++, e += w) {
                var v = tb.wide ? odv.getUint32(e, false) * 4294967296 + odv.getUint32(e + 4, false)
                                : odv.getUint32(e, false);
                if (v < after) continue;
                v += delta;
                if (tb.wide) {
                    odv.setUint32(e, Math.floor(v / 4294967296), false);
                    odv.setUint32(e + 4, v % 4294967296, false);
                } else if (v > 0xFFFFFFFF) {
                    return null;                            // unmarked beats broken
                } else {
                    odv.setUint32(e, v, false);
                }
            }
        }
        return out;
    }

    function markMp4(blob, tool) {
        if (blob.size < 16) return Promise.resolve(blob);
        return mp4Moov(blob).then(function (moov) {
            if (!moov) return blob;
            return bytesOf(blob, moov.start, moov.end).then(function (m) {
                var marked = mp4MarkedMoov(m, moov.start, tool);
                if (!marked) return blob;
                return new Blob([blob.slice(0, moov.start), marked, blob.slice(moov.end)],
                                { type: blob.type || 'video/mp4' });
            });
        });
    }

    /* ---------------- JPEG (APP1/XMP + COM) ---------------- */
    /*
     * JPEG is the DEFAULT output of every AI image tool we ship (photo-restore,
     * photo-colorize, photo-enhancer, object-eraser all call
     * canvas.toBlob('image/jpeg')), and until 2026-08-08 this file could not
     * mark it at all — so adding an __aiMark call to those tools would have been
     * a silent no-op, the same trap AiContentMarker's docblock records for SFX.
     *
     * Two markers, because they serve different readers:
     *   - APP1 XMP with IPTC DigitalSourceType=trainedAlgorithmicMedia — the
     *     machine-readable form Art. 50(2) asks for; exiftool, C2PA-adjacent
     *     tooling and the platforms' own scanners read it.
     *   - COM segment with the human-readable note, mirroring PNG's Comment.
     * Inserted right after SOI: segment order is not significant to decoders,
     * and staying at a fixed offset keeps this walker trivial (a walker that
     * has to understand every APPn is a walker that can get it wrong).
     */

    var XMP_NS = 'http://ns.adobe.com/xap/1.0/';
    var IPTC_TRAINED = 'http://cv.iptc.org/newscodes/digitalsourcetype/trainedAlgorithmicMedia';

    function xmpPacket(tool) {
        return '<?xpacket begin="" id="W5M0MpCehiHzreSzNTczkc9d"?>'
            + '<x:xmpmeta xmlns:x="adobe:ns:meta/">'
            + '<rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">'
            + '<rdf:Description rdf:about=""'
            + ' xmlns:Iptc4xmpExt="http://iptc.org/std/Iptc4xmpExt/2008-02-29/"'
            + ' xmlns:dc="http://purl.org/dc/elements/1.1/">'
            + '<Iptc4xmpExt:DigitalSourceType>' + IPTC_TRAINED + '</Iptc4xmpExt:DigitalSourceType>'
            + '<dc:description>' + noteText(tool) + '</dc:description>'
            + '</rdf:Description></rdf:RDF></x:xmpmeta>'
            + '<?xpacket end="w"?>';
    }

    // APP1 (0xFFE1): marker + length(2, BE, counts itself) + NUL-terminated ns + packet
    function jpegXmpSegment(tool) {
        var ns = enc.encode(XMP_NS), pk = enc.encode(xmpPacket(tool));
        var len = 2 + ns.length + 1 + pk.length;
        if (len > 0xFFFF) return null;
        var out = new Uint8Array(2 + len);
        out[0] = 0xFF; out[1] = 0xE1;
        out[2] = (len >> 8) & 0xff; out[3] = len & 0xff;
        out.set(ns, 4);
        out[4 + ns.length] = 0;
        out.set(pk, 5 + ns.length);
        return out;
    }

    // COM (0xFFFE): marker + length(2, BE, counts itself) + text
    function jpegComSegment(tool) {
        var t = enc.encode(noteText(tool));
        var len = 2 + t.length;
        if (len > 0xFFFF) return null;
        var out = new Uint8Array(2 + len);
        out[0] = 0xFF; out[1] = 0xFE;
        out[2] = (len >> 8) & 0xff; out[3] = len & 0xff;
        out.set(t, 4);
        return out;
    }

    function markJpeg(blob, tool) {
        return blob.arrayBuffer().then(function (buf) {
            var u8 = new Uint8Array(buf);
            if (u8.length < 4) return blob;
            if (u8[0] !== 0xFF || u8[1] !== 0xD8) return blob;      // not SOI
            if (containsAscii(u8, 'DigitalSourceType', 65536)) return blob; // already marked
            var seg = jpegXmpSegment(tool), com = jpegComSegment(tool);
            if (!seg || !com) return blob;
            var out = new Uint8Array(u8.length + seg.length + com.length);
            out.set(u8.subarray(0, 2), 0);
            out.set(seg, 2);
            out.set(com, 2 + seg.length);
            out.set(u8.subarray(2), 2 + seg.length + com.length);
            return new Blob([out], { type: blob.type || 'image/jpeg' });
        });
    }

    /* ---------------- Matroska / WebM (Tags) ---------------- */
    /*
     * WebM is what MediaRecorder hands us on Chrome — i.e. the DEFAULT container
     * for /talking-avatar exports on the most common browser. Until 2026-08-08
     * it passed through unmarked, and the code said so out loud: "WebM passes
     * through (visible on-page notice covers it)". A notice on our page does not
     * travel with the file, and Art. 50(2) asks for the mark to be IN it.
     *
     * Where the Tags go: in front of the first Cluster (webmTagsInFront),
     * where demuxers read a file's tags — the reason, and what moves with
     * them, is at that function. Segment layouts met, all real (02.10.2026):
     *   - Chrome's MediaRecorder: UNKNOWN Segment size (`01 ff ff ff ff ff ff
     *     ff`), Info, Tracks, unknown-size Clusters — no SeekHead, no Cues.
     *   - webm-muxer (WebMEncoder): known size, SeekHead, Cues at the end.
     *   - FFmpeg (every -f webm export, the alpha-WebM remux): known size,
     *     SeekHead + Void, its own Tags, Cues at the end.
     * A layout the front placement does not move falls back to the placement
     * used until then (webmTagsAtEnd): Tags appended at EOF, a known Segment
     * size rewritten in place at the same VINT width.
     * Refused (original returned): a width with no room for the larger value,
     * a known-size Segment that ends before the file does (EOF placement).
     */

    // EBML variable-size integer: value encoded in the fewest octets that fit.
    function vint(n) {
        var octets = 1;
        while (octets <= 8 && n >= Math.pow(2, 7 * octets) - 1) octets++;
        if (octets > 8) return null;
        var out = new Uint8Array(octets);
        for (var i = octets - 1; i >= 0; i--) { out[i] = n & 0xff; n = Math.floor(n / 256); }
        out[0] |= (1 << (8 - octets));
        return out;
    }

    function ebml(idBytes, payload) {
        var size = vint(payload.length);
        if (!size) return null;
        var out = new Uint8Array(idBytes.length + size.length + payload.length);
        out.set(idBytes, 0);
        out.set(size, idBytes.length);
        out.set(payload, idBytes.length + size.length);
        return out;
    }

    function simpleTag(name, value) {
        var n = ebml([0x45, 0xA3], enc.encode(name));      // TagName
        var v = ebml([0x44, 0x87], enc.encode(value));     // TagString
        if (!n || !v) return null;
        return ebml([0x67, 0xC8], concatU8([n, v]));       // SimpleTag
    }

    function buildWebmTags(tool) {
        var targets = ebml([0x63, 0xC0], new Uint8Array(0)); // Targets: whole file
        var parts = [
            targets,
            simpleTag('AI_GENERATED', 'true'),
            simpleTag('DIGITAL_SOURCE_TYPE', 'trainedAlgorithmicMedia'),
            simpleTag('COMMENT', noteText(tool)),
        ];
        for (var i = 0; i < parts.length; i++) { if (!parts[i]) return null; }
        var tag = ebml([0x73, 0x73], concatU8(parts));       // Tag
        if (!tag) return null;
        return ebml([0x12, 0x54, 0xC3, 0x67], tag);          // Tags
    }

    var WEBM_HEAD_BYTES = 262144;
    var EBML_CLUSTER = 0x1F43B675, EBML_SEEKHEAD = 0x114D9B74, EBML_CUES = 0x1C53BB6B,
        EBML_CRC32 = 0xBF, EBML_VOID = 0xEC;
    // Segment positions: SeekPosition, CueClusterPosition, CueCodecState — and
    // the masters that hold them (SeekHead > Seek, Cues > CuePoint >
    // CueTrackPositions, CueReference).
    var EBML_POSITION_IDS = { 0x53AC: 1, 0xF1: 1, 0xEA: 1 };
    var EBML_POSITION_MASTERS = { 0x114D9B74: 1, 0x4DBB: 1, 0x1C53BB6B: 1, 0xBB: 1, 0xB7: 1, 0xDB: 1 };

    // One EBML element header at `o` (no further than `limit`): the ID keeps
    // its marker bits (0x1F43B675), `size` is null for "unknown".
    function ebmlAt(u8, o, limit) {
        if (o >= limit) return null;
        var iw = 0, sw = 0, k;
        for (k = 0; k < 4; k++) { if (u8[o] & (0x80 >> k)) { iw = k + 1; break; } }
        if (!iw || o + iw >= limit) return null;
        var id = 0;
        for (k = 0; k < iw; k++) id = id * 256 + u8[o + k];
        var so = o + iw;
        for (k = 0; k < 8; k++) { if (u8[so] & (0x80 >> k)) { sw = k + 1; break; } }
        if (!sw || so + sw > limit) return null;
        var mask = (0x80 >> (sw - 1)) - 1, v = u8[so] & mask, ones = v === mask;
        for (k = 1; k < sw; k++) { v = v * 256 + u8[so + k]; if (u8[so + k] !== 0xFF) ones = false; }
        return { id: id, at: o, sizeAt: so, sizeLen: sw, size: ones ? null : v,
                 dataAt: so + sw, end: ones ? null : so + sw + v };
    }

    // An unsigned-integer element's value (exact below 2^53; null past it).
    function ebmlUint(u8, el) {
        if (el.size > 8) return null;
        var v = 0;
        for (var k = 0; k < el.size; k++) v = v * 256 + u8[el.dataAt + k];
        return v <= 9007199254740991 ? v : null;
    }

    // A size VINT of at least `width` octets (wider when `n` needs it).
    function ebmlVint(n, width) {
        var w = Math.max(1, width || 1);
        while (w < 8 && n >= Math.pow(2, 7 * w) - 1) w++;
        if (n >= Math.pow(2, 7 * w) - 1) return null;
        var out = new Uint8Array(w);
        for (var i = w - 1, v = n; i >= 0; i--) { out[i] = v % 256; v = Math.floor(v / 256); }
        out[0] |= 0x80 >> (w - 1);
        return out;
    }

    function ebmlUintBytes(v, width) {
        var w = Math.max(1, width || 1);
        while (w < 8 && v >= Math.pow(2, 8 * w)) w++;
        var out = new Uint8Array(w);
        for (var i = w - 1; i >= 0; i--) { out[i] = v % 256; v = Math.floor(v / 256); }
        return out;
    }

    function ebmlWrap(idBytes, body, sizeWidth) {
        var size = ebmlVint(body.length, sizeWidth);
        return size ? concatU8([idBytes, size, body]) : null;
    }

    // A Void element exactly `total` bytes long (total ≥ 2).
    function ebmlVoid(total) {
        for (var w = 1; w <= 8; w++) {
            var payload = total - 1 - w;
            if (payload >= 0 && payload < Math.pow(2, 7 * w) - 1) {
                var out = new Uint8Array(total);
                out[0] = EBML_VOID;
                out.set(ebmlVint(payload, w), 1);
                return out;
            }
        }
        return null;
    }

    /* The master `el` of `u8` (a SeekHead or Cues) rebuilt with every Segment
       position mapped by `move`: at its own width when the new value fits,
       wider when not (an EBML uint takes any width) — each master's size is
       rewritten and a CRC-32 that opens it recomputed. Null: not walkable. */
    function ebmlRebuild(u8, el, move) {
        var parts = [], crc = false;
        for (var p = el.dataAt; p < el.end;) {
            var c = ebmlAt(u8, p, el.end);
            if (!c || c.size === null || c.end > el.end) return null;
            if (c.id === EBML_CRC32 && p === el.dataAt && c.size === 4) crc = true;
            else if (EBML_POSITION_MASTERS[c.id]) {
                var r = ebmlRebuild(u8, c, move);
                if (!r) return null;
                parts.push(r);
            } else if (EBML_POSITION_IDS[c.id]) {
                var v = ebmlUint(u8, c);
                if (v === null) return null;
                // CueCodecState 0 means "the TrackEntry's own CodecPrivate", not a position.
                if (!(c.id === 0xEA && v === 0)) v = move(v);
                var re = ebmlWrap(u8.subarray(c.at, c.sizeAt), ebmlUintBytes(v, c.size), 1);
                if (!re) return null;
                parts.push(re);
            } else {
                parts.push(u8.subarray(c.at, c.end));
            }
            p = c.end;
        }
        var body = concatU8(parts);
        if (crc) {
            var sum = crc32(body);
            body = concatU8([new Uint8Array([EBML_CRC32, 0x84, sum & 0xff, (sum >>> 8) & 0xff,
                                              (sum >>> 16) & 0xff, (sum >>> 24) & 0xff]), body]);
        }
        return ebmlWrap(u8.subarray(el.at, el.sizeAt), body, el.sizeLen);
    }

    // [{id, pos}] of a SeekHead's entries (pos relative to the Segment data).
    function ebmlSeekEntries(u8, sh) {
        var out = [];
        for (var p = sh.dataAt; p < sh.end;) {
            var s = ebmlAt(u8, p, sh.end);
            if (!s || s.size === null || s.end > sh.end) return null;
            if (s.id === 0x4DBB) {
                var id = null, pos = null;
                for (var q = s.dataAt; q < s.end;) {
                    var c = ebmlAt(u8, q, s.end);
                    if (!c || c.size === null || c.end > s.end) return null;
                    if (c.id === 0x53AB) id = ebmlUint(u8, c);
                    if (c.id === 0x53AC) pos = ebmlUint(u8, c);
                    q = c.end;
                }
                if (id !== null && pos !== null) out.push({ id: id, pos: pos });
            }
            p = s.end;
        }
        return out;
    }

    /* Where a demuxer looks for a file's tags is the Segment's head: FFmpeg
       reads the level-1 elements up to the first Cluster and the rest only
       through the SeekHead. Appended at EOF (until 02.10.2026), our Tags were
       in every marked WebM and ffprobe showed none of them — on MediaRecorder,
       webm-muxer and FFmpeg files alike. So they go in front of the first
       Cluster, and every Segment position past that point moves by their
       length: SeekHead SeekPosition, Cues CueClusterPosition/CueCodecState.
       A value that outgrows its width is written wider: a SeekHead that grows
       takes the room from the Void its writer left right after it (Chromium's
       finalized recording has 1-byte positions and a 48-byte Void; FFmpeg a
       97-byte one), a Cues past the clusters just grows, and what follows it
       moves too. Reads the head and each SeekHead/Cues the SeekHeads point
       past it (the Cues of a long video: tens of KB) — never a Cluster.
       Null → a layout this does not move (a Cluster stating its own Position,
       a head longer than the read, a grown SeekHead with no Void to take it):
       the caller appends instead. */
    function webmTagsInFront(blob, head, tags) {
        var none = Promise.resolve(null);
        var e = ebmlAt(head, 0, head.length);
        if (!e || e.id !== 0x1A45DFA3 || e.size === null) return none;
        var seg = ebmlAt(head, e.end, head.length);
        if (!seg || seg.id !== 0x18538067) return none;
        var els = [], cluster = null;
        for (var p = seg.dataAt; !cluster;) {
            var c = ebmlAt(head, p, head.length);
            if (!c) return none;
            if (c.id === EBML_CLUSTER) { cluster = c; break; }
            if (c.size === null || c.end > head.length) return none;
            els.push(c);
            p = c.end;
        }
        // A Cluster that states its own Segment position would hold a stale one.
        for (var q = cluster.dataAt, n = 0; n < 4; n++) {
            var k = ebmlAt(head, q, head.length);
            if (!k || k.id === 0xA3 || k.id === 0xA0 || k.size === null) break;   // the first block
            if (k.id === 0xA7) return none;
            q = k.end;
        }
        var from = cluster.at - seg.dataAt, T = tags.length;

        // SeekHeads and Cues past the head, as the SeekHeads name them (and as a
        // SeekHead found there names more).
        var later = [], seen = {};
        var name = function (entries) {
            (entries || []).forEach(function (x) {
                var at = seg.dataAt + x.pos;
                if ((x.id === EBML_SEEKHEAD || x.id === EBML_CUES) && x.pos >= from && !seen[at]) { seen[at] = x.id; later.push(at); }
            });
        };
        for (var i = 0; i < els.length; i++) {
            if (els[i].id === EBML_SEEKHEAD) { var en = ebmlSeekEntries(head, els[i]); if (!en) return none; name(en); }
        }
        var fetched = [];
        var fetchNext = function () {
            if (!later.length) return Promise.resolve(true);
            if (fetched.length >= 8) return Promise.resolve(false);
            var at = later.shift();
            return bytesOf(blob, at, Math.min(blob.size, at + 12)).then(function (h) {
                var el = ebmlAt(h, 0, h.length);
                if (!el || el.id !== seen[at] || el.size === null || at + el.end > blob.size || el.end > 64 * 1048576) return false;
                return bytesOf(blob, at, at + el.end).then(function (b) {
                    var mel = ebmlAt(b, 0, b.length);
                    if (!mel || mel.end !== b.length) return false;
                    if (mel.id === EBML_SEEKHEAD) { var en2 = ebmlSeekEntries(b, mel); if (!en2) return false; name(en2); }
                    fetched.push({ at: at, end: at + mel.end, bytes: b, el: mel, grow: 0 });
                    return fetchNext();
                });
            });
        };
        return fetchNext().then(function (ok) {
            if (!ok) return null;
            fetched.sort(function (a, b) { return a.at - b.at; });
            // Old Segment position → new: in front of the Tags nothing moves (a
            // grown head element is absorbed by its Void); past them, the Tags
            // and every rebuilt element that ends before the position.
            var move = function (pos) {
                if (pos < from) return pos;
                var add = T;
                for (var f = 0; f < fetched.length; f++) if (fetched[f].end - seg.dataAt <= pos) add += fetched[f].grow;
                return pos + add;
            };
            for (var round = 0, changed = true; changed; round++) {
                if (round > 4) return null;
                changed = false;
                for (var f = 0; f < fetched.length; f++) {
                    var r = ebmlRebuild(fetched[f].bytes, fetched[f].el, move);
                    if (!r) return null;
                    var g = r.length - (fetched[f].end - fetched[f].at);
                    fetched[f].out = r;
                    if (g !== fetched[f].grow) { fetched[f].grow = g; changed = true; }
                }
            }
            var parts = [head.subarray(0, seg.dataAt)];
            for (var j = 0; j < els.length; j++) {
                var x = els[j];
                if (x.taken) continue;
                if (x.id !== EBML_SEEKHEAD && x.id !== EBML_CUES) { parts.push(head.subarray(x.at, x.end)); continue; }
                var nb = ebmlRebuild(head, x, move);
                if (!nb) return null;
                var grew = nb.length - (x.end - x.at);
                parts.push(nb);
                if (grew === 0) continue;
                var vd = els[j + 1], room = vd && vd.id === EBML_VOID && vd.at === x.end ? (vd.end - vd.at) - grew : -1;
                if (room < 0 || room === 1) return null;
                if (room > 0) parts.push(ebmlVoid(room));
                vd.taken = true;
            }
            parts.push(tags);
            var top = concatU8(parts);
            if (seg.size !== null) {
                var size = seg.size + T;
                fetched.forEach(function (m) { size += m.grow; });
                var sz = ebmlVint(size, seg.sizeLen);
                if (!sz || sz.length !== seg.sizeLen) return null;
                top.set(sz, seg.sizeAt);
            }
            var out = [top], cursor = cluster.at;
            fetched.forEach(function (m) { out.push(blob.slice(cursor, m.at), m.out); cursor = m.end; });
            out.push(blob.slice(cursor));
            return new Blob(out, { type: blob.type || 'video/webm' });
        });
    }

    // The placement before 02.10.2026, kept for layouts webmTagsInFront does
    // not move: Tags appended at EOF, a known Segment size rewritten in place.
    function webmTagsAtEnd(blob, head, tags) {
        var i = findBytes(head, [0x18, 0x53, 0x80, 0x67], 0, 4096);
        if (i < 0) return blob;
        var sizeOff = i + 4;
        if (sizeOff >= head.length) return blob;
        var first = head[sizeOff];
        var width = 0;
        for (var b = 0; b < 8; b++) { if (first & (1 << (7 - b))) { width = b + 1; break; } }
        if (!width || sizeOff + width > head.length) return blob;
        var dataAt = sizeOff + width;
        var top = head.slice(0, dataAt);

        // Read the declared size; all value bits set = "unknown" = runs to EOF,
        // and an unknown-size Segment needs no patching at all.
        var headBits = (1 << (8 - width)) - 1;
        var val = first & headBits, allOnes = (val === headBits);
        for (var k = 1; k < width; k++) {
            val = val * 256 + head[sizeOff + k];
            if (head[sizeOff + k] !== 0xFF) allOnes = false;
        }
        if (!allOnes) {
            // Tags appended at EOF are inside a known-size Segment only when
            // the Segment ends there; grown otherwise, it would swallow
            // whatever follows it.
            if (dataAt + val !== blob.size) return blob;
            var grown = val + tags.length;
            // Math.pow, not <<: 7*8 = 56 value bits overflow a 32-bit shift.
            var maxVal = Math.pow(2, 7 * width) - 1;   // all-ones is reserved for "unknown"
            if (grown >= maxVal) return blob;
            var v = grown;
            for (var j = width - 1; j >= 0; j--) { top[sizeOff + j] = v & 0xff; v = Math.floor(v / 256); }
            top[sizeOff] |= (1 << (8 - width));
        }
        return new Blob([top, blob.slice(dataAt), tags], { type: blob.type || 'video/webm' });
    }

    function markWebm(blob, tool) {
        var size = blob.size;
        if (size < 8) return Promise.resolve(blob);
        return Promise.all([bytesOf(blob, 0, Math.min(size, WEBM_HEAD_BYTES)),
                            bytesOf(blob, Math.max(0, size - 65536), size)]).then(function (r) {
            var head = r[0];
            // EBML header magic
            if (head[0] !== 0x1A || head[1] !== 0x45 || head[2] !== 0xDF || head[3] !== 0xA3) return blob;
            // Ours sit in front of the first Cluster; a file marked before
            // 02.10.2026 carries them at the end.
            if (containsAscii(head, 'DIGITAL_SOURCE_TYPE') || containsAscii(r[1], 'DIGITAL_SOURCE_TYPE')) return blob;
            var tags = buildWebmTags(tool);
            if (!tags) return blob;
            return Promise.resolve().then(function () { return webmTagsInFront(blob, head, tags); })
                .catch(function () { return null; })
                .then(function (front) { return front || webmTagsAtEnd(blob, head, tags); });
        });
    }

    /* ---------------- WebP (VP8X + XMP chunk) ---------------- */
    /*
     * WebP is an OUTPUT OPTION of four marked tools (/photo-colorize,
     * /photo-enhancer, /object-eraser, /stereo-pictures) — checked in their code,
     * not assumed — so leaving it unmarked meant a user who picks WebP ships an
     * unmarked file. It is also RIFF, like WAV, which is why it used to be handed
     * to the WAV writer and silently returned untouched.
     *
     * Metadata requires the EXTENDED format: a VP8X header chunk whose flags say
     * an 'XMP ' chunk is present. A canvas-encoded WebP is the SIMPLE form
     * (a bare VP8 /VP8L chunk), so the canvas size is read out of the bitstream
     * and the file is rebuilt as VP8X + original image chunks + XMP.
     * Refused (original returned): anything whose dimensions we cannot read —
     * a wrong canvas size in VP8X would corrupt the image, and an unmarked file
     * beats a broken one.
     */

    function riffChunk(fourcc, payload) {
        var pad = payload.length & 1;                 // chunks are padded to even length
        var out = new Uint8Array(8 + payload.length + pad);
        for (var i = 0; i < 4; i++) out[i] = fourcc.charCodeAt(i);
        new DataView(out.buffer).setUint32(4, payload.length, true);
        out.set(payload, 8);
        return out;
    }

    function webpDims(fourcc, p) {
        if (fourcc === 'VP8 ') {
            // keyframe: 3-byte frame tag, then the 0x9D 0x01 0x2A start code
            if (p.length < 10 || p[3] !== 0x9D || p[4] !== 0x01 || p[5] !== 0x2A) return null;
            return { w: ((p[7] << 8) | p[6]) & 0x3FFF, h: ((p[9] << 8) | p[8]) & 0x3FFF, alpha: false };
        }
        if (fourcc === 'VP8L') {
            if (p.length < 5 || p[0] !== 0x2F) return null;
            var bits = (p[1] | (p[2] << 8) | (p[3] << 16) | (p[4] << 24)) >>> 0;
            return { w: (bits & 0x3FFF) + 1, h: ((bits >>> 14) & 0x3FFF) + 1, alpha: !!((bits >>> 28) & 1) };
        }
        return null;
    }

    function markWebp(blob, tool) {
        return blob.arrayBuffer().then(function (buf) {
            var u8 = new Uint8Array(buf);
            if (u8.length < 16) return blob;
            var dv = new DataView(buf);
            if (dv.getUint32(0, false) !== 0x52494646) return blob;      // 'RIFF'
            if (dv.getUint32(8, false) !== 0x57454250) return blob;      // 'WEBP'
            if (containsAscii(u8, 'DigitalSourceType', u8.length)) return blob;

            var fourccAt = function (o) { return String.fromCharCode(u8[o], u8[o + 1], u8[o + 2], u8[o + 3]); };
            var first = fourccAt(12);
            var firstSize = dv.getUint32(16, true);
            if (20 + firstSize > u8.length) return blob;                 // truncated
            var xmp = riffChunk('XMP ', enc.encode(xmpPacket(tool)));
            var body;

            if (first === 'VP8X') {
                if (firstSize < 10) return blob;
                body = u8.slice(12);                                     // every chunk, verbatim
                body[8] |= 0x04;                                         // flags: XMP present
                body = concatU8([body, xmp]);
            } else {
                var dims = webpDims(first, u8.subarray(20, 20 + firstSize));
                if (!dims || !dims.w || !dims.h) return blob;
                var vp8x = new Uint8Array(10);
                vp8x[0] = 0x04 | (dims.alpha ? 0x10 : 0);                // XMP (+ alpha when the source has it)
                var w = dims.w - 1, h = dims.h - 1;
                vp8x[4] = w & 0xff; vp8x[5] = (w >> 8) & 0xff; vp8x[6] = (w >> 16) & 0xff;
                vp8x[7] = h & 0xff; vp8x[8] = (h >> 8) & 0xff; vp8x[9] = (h >> 16) & 0xff;
                body = concatU8([riffChunk('VP8X', vp8x), u8.slice(12), xmp]);
            }

            var out = new Uint8Array(12 + body.length);
            out.set(u8.subarray(0, 12), 0);
            out.set(body, 12);
            new DataView(out.buffer).setUint32(4, out.length - 8, true); // RIFF size
            return new Blob([out], { type: blob.type || 'image/webp' });
        });
    }

    /* ---------------- Ogg (Vorbis/Opus comment header) ---------------- */
    /*
     * /text-to-speech and /interview-studio can hand back Ogg/Opus — again
     * checked in their code. Ogg keeps metadata in the codec's COMMENT HEADER,
     * so the marker goes in as ordinary user comments and the carrying page is
     * rebuilt: new lacing table, new CRC (Ogg's own CRC-32: poly 0x04C11DB7,
     * no reflection, no final xor, computed with the CRC field zeroed).
     * Refused (original returned): a comment packet that does not END on its
     * page, or one that would need more than the 255 segments a page can hold —
     * both would mean re-paginating the stream, and a stream we re-paginate
     * wrongly is a file the user cannot play.
     */

    var oggCrcTable = null;
    function oggCrc32(bytes) {
        if (!oggCrcTable) {
            oggCrcTable = new Uint32Array(256);
            for (var i = 0; i < 256; i++) {
                var r = i << 24;
                for (var j = 0; j < 8; j++) r = (r & 0x80000000) ? ((r << 1) ^ 0x04c11db7) : (r << 1);
                oggCrcTable[i] = r >>> 0;
            }
        }
        var crc = 0;
        for (var k = 0; k < bytes.length; k++) {
            crc = ((crc << 8) ^ oggCrcTable[((crc >>> 24) & 0xff) ^ bytes[k]]) >>> 0;
        }
        return crc >>> 0;
    }

    function u32le(n) { return [n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff]; }

    var OGG_HEAD_BYTES = 262144;

    function markOgg(blob, tool) {
        if (blob.size < 27) return Promise.resolve(blob);
        // The comment header is the stream's second packet, on its first pages;
        // a page is at most 65 307 bytes, so this read holds it with room to
        // spare. The audio after it is never read.
        return bytesOf(blob, 0, Math.min(blob.size, OGG_HEAD_BYTES)).then(function (u8) {
            if (u8[0] !== 0x4F || u8[1] !== 0x67 || u8[2] !== 0x67 || u8[3] !== 0x53) return blob; // 'OggS'

            var off = 0;
            while (off + 27 <= u8.length) {
                if (u8[off] !== 0x4F || u8[off + 1] !== 0x67 || u8[off + 2] !== 0x67 || u8[off + 3] !== 0x53) return blob;
                var nsegs = u8[off + 26];
                var tableAt = off + 27, dataAt = tableAt + nsegs;
                if (dataAt > u8.length) return blob;
                var plen = 0;
                for (var s = 0; s < nsegs; s++) plen += u8[tableAt + s];
                var pageEnd = dataAt + plen;
                if (pageEnd > u8.length) return blob;
                var first = u8.subarray(dataAt, pageEnd);

                var isOpus = first.length >= 8 && String.fromCharCode.apply(null, first.subarray(0, 8)) === 'OpusTags';
                var isVorbis = first.length >= 7 && first[0] === 3 &&
                               String.fromCharCode.apply(null, first.subarray(1, 7)) === 'vorbis';
                if (!isOpus && !isVorbis) { off = pageEnd; continue; }

                // The comment packet opens the page and ends at its first lacing
                // value under 255. What follows it on the page — the Vorbis
                // setup header, as libvorbis and FFmpeg lay it out — stays, with
                // its own lacing values. (Rebuilt as the only packet, the page
                // lost the setup header and the file did not open: 02.10.2026.)
                var last = -1, clen = 0;
                for (s = 0; s < nsegs; s++) { clen += u8[tableAt + s]; if (u8[tableAt + s] < 255) { last = s; break; } }
                if (last < 0) return blob;   // the comment packet continues onto the next page
                var p = u8.subarray(dataAt, dataAt + clen);
                if (containsAscii(p, 'DIGITAL_SOURCE_TYPE')) return blob;   // already marked
                var restLace = u8.subarray(tableAt + last + 1, dataAt), rest = u8.subarray(dataAt + clen, pageEnd);

                var pre = isOpus ? 8 : 7;
                var rd = pre;
                var rdU32 = function () {
                    var v = (p[rd] | (p[rd + 1] << 8) | (p[rd + 2] << 16) | (p[rd + 3] << 24)) >>> 0;
                    rd += 4; return v;
                };
                if (rd + 4 > p.length) return blob;
                var vlen = rdU32();
                if (rd + vlen + 4 > p.length) return blob;
                var vendor = p.subarray(rd, rd + vlen); rd += vlen;
                var count = rdU32();
                var items = [];
                for (var c = 0; c < count; c++) {
                    if (rd + 4 > p.length) return blob;
                    var ilen = rdU32();
                    if (rd + ilen > p.length) return blob;
                    items.push(p.subarray(rd, rd + ilen)); rd += ilen;
                }
                items.push(enc.encode('AI_GENERATED=true'));
                items.push(enc.encode('DIGITAL_SOURCE_TYPE=trainedAlgorithmicMedia'));
                items.push(enc.encode('COMMENT=' + noteText(tool)));

                var parts = [p.subarray(0, pre), new Uint8Array(u32le(vlen)), vendor,
                             new Uint8Array(u32le(items.length))];
                items.forEach(function (it) { parts.push(new Uint8Array(u32le(it.length))); parts.push(it); });
                if (isVorbis) parts.push(new Uint8Array([0x01]));                // framing bit
                // RFC 7845 §5.2: data after the Opus comment list whose first
                // byte has bit 0 set is to be kept; the rest is padding.
                else if (rd < p.length && (p[rd] & 1)) parts.push(p.subarray(rd));
                var np = concatU8(parts);

                var newSegs = Math.floor(np.length / 255) + 1 + restLace.length;   // trailing <255 lacing value
                if (newSegs > 255) return blob;
                var table = new Uint8Array(newSegs), npSegs = newSegs - restLace.length;
                for (var t = 0; t < npSegs - 1; t++) table[t] = 255;
                table[npSegs - 1] = np.length % 255;
                table.set(restLace, npSegs);

                var head = u8.slice(off, off + 27);
                head[26] = newSegs;
                head[22] = head[23] = head[24] = head[25] = 0;           // CRC field zeroed before computing
                var page = concatU8([head, table, np, rest]);
                var crc = oggCrc32(page);
                page[22] = crc & 0xff; page[23] = (crc >>> 8) & 0xff;
                page[24] = (crc >>> 16) & 0xff; page[25] = (crc >>> 24) & 0xff;

                return new Blob([blob.slice(0, off), page, blob.slice(pageEnd)],
                                { type: blob.type || 'audio/ogg' });
            }
            return blob;
        });
    }

    /* ---------------- shared byte helpers ---------------- */

    var NOTE_PREFIX = 'AI-generated content (tool:';

    function bytesOf(blob, from, to) {
        return blob.slice(from, to).arrayBuffer().then(function (b) { return new Uint8Array(b); });
    }

    function fourcc(u8, off) {
        return String.fromCharCode(u8[off], u8[off + 1], u8[off + 2], u8[off + 3]);
    }

    function containsAscii(u8, needle, limit) {
        return findBytes(u8, Array.prototype.map.call(needle, function (c) { return c.charCodeAt(0); }),
                         0, Math.min(limit || u8.length, u8.length)) >= 0;
    }

    function findBytes(u8, pat, from, to) {
        var end = Math.min(to === undefined ? u8.length : to, u8.length) - pat.length;
        for (var i = from || 0; i <= end; i++) {
            var ok = true;
            for (var j = 0; j < pat.length; j++) { if (u8[i + j] !== pat[j]) { ok = false; break; } }
            if (ok) return i;
        }
        return -1;
    }

    /* ---------------- public API ---------------- */

    /* moov-level `udta` → the same udta carrying our comment items: the one
       insertion behind markMp4 and behind a writer that assembles a NEW moov
       from byte ranges (MP4TrackRemux.replaceAudio, opts.udta — /video-dubbing
       splices the dubbed track into the source and marks it on the way).
       `udta` is the box bytes (header included) or null when the movie has
       none; the result is the new box. Items go in at the deepest existing
       level of udta > meta > ilst (FFmpeg writes an encoder tag there); an
       existing meta it cannot walk (QuickTime-style, not a FullBox) gets a
       fresh iTunes-style meta appended after it rather than no mark.
       Idempotent; anything malformed comes back unchanged. */
    function udtaWithMark(udta, tool) {
        var items = concatU8([
            ilstItem('©cmt', noteText(tool)),
            ilstItem('©too', 'Timbrica AI tools'),
        ]);
        var freshMeta = function () {
            return box('meta', concatU8([new Uint8Array(4), box('hdlr', metaHdlr()), box('ilst', items)]));
        };
        if (!udta || udta.length < 8) return box('udta', freshMeta());
        try {
            if (new TextDecoder('latin1').decode(udta).indexOf(NOTE_PREFIX) !== -1) return udta;
        } catch (e) { /* no latin1 decoder — proceed */ }
        var dv = new DataView(udta.buffer, udta.byteOffset, udta.byteLength);
        var t4 = function (o) { return String.fromCharCode(udta[o], udta[o + 1], udta[o + 2], udta[o + 3]); };
        if (dv.getUint32(0, false) !== udta.length || t4(4) !== 'udta') return udta;
        var metaAt = -1, metaSize = 0, ilstAt = -1, ilstSize = 0, walkable = true;
        for (var p = 8; p + 8 <= udta.length;) {
            var s = dv.getUint32(p, false);
            if (s < 8 || p + s > udta.length) return udta;
            if (t4(p + 4) === 'meta' && metaAt < 0) {
                metaAt = p; metaSize = s;
                for (var q = p + 12; q + 8 <= p + s;) {
                    var s2 = dv.getUint32(q, false);
                    if (s2 < 8 || q + s2 > p + s) { walkable = false; break; }
                    if (t4(q + 4) === 'ilst') { ilstAt = q; ilstSize = s2; }
                    q += s2;
                }
            }
            p += s;
        }
        var insertAt, ins, bump;
        if (metaAt < 0 || !walkable) { insertAt = udta.length; ins = freshMeta(); bump = [0]; }
        else if (ilstAt < 0) { insertAt = metaAt + metaSize; ins = box('ilst', items); bump = [0, metaAt]; }
        else { insertAt = ilstAt + ilstSize; ins = items; bump = [0, metaAt, ilstAt]; }
        var out = new Uint8Array(udta.length + ins.length);
        out.set(udta.subarray(0, insertAt), 0);
        out.set(ins, insertAt);
        out.set(udta.subarray(insertAt), insertAt + ins.length);
        var odv = new DataView(out.buffer);
        bump.forEach(function (b) { odv.setUint32(b, dv.getUint32(b, false) + ins.length, false); });
        return out;
    }

    function safe(fn, blob, tool) {
        try {
            return Promise.resolve(fn(blob, tool)).catch(function () { return blob; });
        } catch (e) {
            return Promise.resolve(blob);
        }
    }

    /* ================= label(): a mark the AUTHOR declares (/ai-label) ================= */
    /*
     * Everything above marks OUR generators' outputs — files that leave a canvas or a muxer with
     * no metadata of their own — so it may insert at a fixed place and skip a file that already
     * carries something. /ai-label marks a file the PERSON brings: a camera JPEG with Lightroom
     * XMP, an MP3 with its ID3 tag, a phone video. These writers therefore MERGE: the person's
     * XMP, ID3 frames and chunks stay; ours join them; a value this tool manages (the IPTC
     * DigitalSourceType, the AIGC field) REPLACES the old one rather than standing next to it —
     * one file, one answer. No pixel, sample or frame is re-encoded here: a visible label is the
     * page's business and happens before this.
     *
     * label(blob, filename, spec) → Promise<{blob, changed, writes:[{where, field, value}], refused}>
     *   spec.dst       'trainedAlgorithmicMedia' (fully generated) | 'compositeWithTrainedAlgorithmicMedia'
     *                  (partly generated / edited with a generative model) — IPTC NewsCodes;
     *   spec.aiSystem  free text, IPTC 2025.1 Iptc4xmpExt:AISystemUsed («Midjourney v7»), optional;
     *   spec.aigc      {Label, ContentProducer, ProduceID, ReservedCode1, ContentPropagator,
     *                  PropagateID, ReservedCode2} — China GB 45438-2025 implicit label, optional;
     *   spec.stamp     what the Timbrica namespace records (who declared it and when).
     * `refused` names why nothing was written (the blob is then the original): unknown
     * container, a tag layout this does not rewrite, an XMP that would not fit its segment.
     * Never throws; never returns a broken file. Time-based files (MP4, WAV, MP3, WebM) are
     * read by their headers only, like the door's writers; images are read whole.
     */

    var NS_IPTC_EXT = 'http://iptc.org/std/Iptc4xmpExt/2008-02-29/';
    var NS_TC260 = 'http://www.tc260.org.cn/ns/AIGC/1.0/';
    var NS_TMB_LABEL = 'https://timbrica.com/ns/ai-label/1.0/';
    var DST_BASE = 'http://cv.iptc.org/newscodes/digitalsourcetype/';
    var DST_ALLOWED = { trainedAlgorithmicMedia: 1, compositeWithTrainedAlgorithmicMedia: 1 };
    var AIGC_KEYS = ['Label', 'ContentProducer', 'ProduceID', 'ReservedCode1', 'ContentPropagator', 'PropagateID', 'ReservedCode2'];
    var XMP_UUID = [0xBE, 0x7A, 0xCF, 0xCB, 0x97, 0xA9, 0x42, 0xE8, 0x9C, 0x71, 0x99, 0x94, 0x91, 0xE3, 0xAF, 0xAC];
    var dec = (typeof TextDecoder !== 'undefined') ? new TextDecoder('utf-8') : null;

    function xmlEsc(s) {
        return String(s).replace(/[&<>"']/g, function (c) {
            return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' }[c];
        });
    }

    function labelSpec(spec) {
        spec = spec || {};
        var clean = function (v, max) { return String(v == null ? '' : v).replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, max || 200); };
        var aigc = null;
        if (spec.aigc) {
            aigc = {};
            AIGC_KEYS.forEach(function (k) { aigc[k] = clean(spec.aigc[k], 120); });
            if (!/^[123]$/.test(aigc.Label)) aigc.Label = '1';
        }
        // день метки — по часам человека (window.__localYmd), не по Гринвичу
        var today = (typeof window !== 'undefined' && window.__localYmd) ? window.__localYmd() : (function (d) {
            var m = d.getMonth() + 1, x = d.getDate();
            return d.getFullYear() + '-' + (m < 10 ? '0' : '') + m + '-' + (x < 10 ? '0' : '') + x;
        })(new Date());
        return {
            dst: DST_ALLOWED[spec.dst] ? spec.dst : 'trainedAlgorithmicMedia',
            aiSystem: clean(spec.aiSystem, 120),
            aigc: aigc,
            stamp: clean(spec.stamp, 160) || ('Declared ' + (spec.dst === 'compositeWithTrainedAlgorithmicMedia' ? 'AI-modified' : 'AI-generated')
                + ' by the author; labelled with Timbrica ai-label, ' + today),
            // The XMP to start from when the file has none of its own — the source's packet
            // after a re-encode dropped it (the visible-label lane of /ai-label).
            baseXmp: typeof spec.baseXmp === 'string' && spec.baseXmp.length < 4 * 1048576 ? spec.baseXmp : null
        };
    }

    function aigcJson(s) { return s.aigc ? JSON.stringify(s.aigc) : ''; }

    // The rdf:Description this tool owns — its own element, so the person's
    // descriptions stay byte for byte (several Descriptions about "" are one
    // resource in RDF, and every XMP reader we tried takes them so).
    function labelDescription(s) {
        return '<rdf:Description rdf:about=""'
            + ' xmlns:Iptc4xmpExt="' + NS_IPTC_EXT + '"'
            + (s.aigc ? ' xmlns:TC260="' + NS_TC260 + '"' : '')
            + ' xmlns:tmbai="' + NS_TMB_LABEL + '">'
            + '<Iptc4xmpExt:DigitalSourceType>' + DST_BASE + s.dst + '</Iptc4xmpExt:DigitalSourceType>'
            + (s.aiSystem ? '<Iptc4xmpExt:AISystemUsed>' + xmlEsc(s.aiSystem) + '</Iptc4xmpExt:AISystemUsed>' : '')
            + (s.aigc ? '<TC260:AIGC>' + xmlEsc(aigcJson(s)) + '</TC260:AIGC>' : '')
            + '<tmbai:Declared>' + xmlEsc(s.stamp) + '</tmbai:Declared>'
            + '</rdf:Description>';
    }

    function labelWrites(s, where) {
        var w = [{ where: where, field: 'Iptc4xmpExt:DigitalSourceType', value: s.dst }];
        if (s.aiSystem) w.push({ where: where, field: 'Iptc4xmpExt:AISystemUsed', value: s.aiSystem });
        if (s.aigc) w.push({ where: where, field: 'TC260:AIGC', value: aigcJson(s) });
        return w;
    }

    function reEscape(s) { return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

    // Prefixes a packet binds to `ns` (a writer may call IPTC Ext anything).
    function prefixesFor(packet, ns) {
        var out = [], re = new RegExp('xmlns:([A-Za-z_][\\w.-]*)\\s*=\\s*["\']' + reEscape(ns) + '["\']', 'g'), m;
        while ((m = re.exec(packet))) if (out.indexOf(m[1]) < 0) out.push(m[1]);
        return out;
    }

    var MANAGED_PROPS = [[NS_IPTC_EXT, 'DigitalSourceType'], [NS_IPTC_EXT, 'AISystemUsed'], [NS_TC260, 'AIGC'], [NS_TMB_LABEL, 'Declared']];

    /* `packet` (string or null) with this tool's properties — the old values of
       the managed ones removed wherever they were (element or attribute form),
       ours in their own Description before </rdf:RDF>. Whitespace padding the
       writer reserved in front of <?xpacket end is given back for what we add, so
       a packet that had room keeps its size. Null: not XMP we can edit. */
    function xmpMerge(packet, s) {
        var desc = labelDescription(s);
        if (!packet) {
            return '<?xpacket begin="\uFEFF" id="W5M0MpCehiHzreSzNTczkc9d"?>'
                + '<x:xmpmeta xmlns:x="adobe:ns:meta/"><rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">'
                + desc + '</rdf:RDF></x:xmpmeta><?xpacket end="w"?>';
        }
        var p = String(packet);
        var close = p.lastIndexOf('</rdf:RDF>');
        if (close < 0 || p.indexOf('<rdf:RDF') < 0) return null;
        var before = enc.encode(p).length;
        // Our own Description from an earlier pass goes whole — not its emptied shell.
        prefixesFor(p, NS_TMB_LABEL).forEach(function (pre) {
            p = p.replace(new RegExp('<rdf:Description\\b[^>]*\\sxmlns:' + reEscape(pre) + '\\s*=\\s*["\']' + reEscape(NS_TMB_LABEL) + '["\'][^>]*>[\\s\\S]*?</rdf:Description>', 'g'), '');
        });
        MANAGED_PROPS.forEach(function (np) {
            prefixesFor(p, np[0]).forEach(function (pre) {
                var tag = reEscape(pre) + ':' + np[1];
                p = p.replace(new RegExp('<' + tag + '\\b[^>]*?/>', 'g'), '')
                     .replace(new RegExp('<' + tag + '\\b[^>]*>[\\s\\S]*?</' + tag + '\\s*>', 'g'), '')
                     .replace(new RegExp('\\s' + tag + '\\s*=\\s*("[^"]*"|\'[^\']*\')', 'g'), '');
            });
        });
        close = p.lastIndexOf('</rdf:RDF>');
        p = p.slice(0, close) + desc + p.slice(close);
        var grew = enc.encode(p).length - before;
        var end = p.lastIndexOf('<?xpacket end');
        if (grew > 0 && end > 0) {
            var ws = 0;
            while (ws < end && /\s/.test(p.charAt(end - 1 - ws))) ws++;
            var take = Math.min(grew, Math.max(0, ws - 1));
            if (take > 0) p = p.slice(0, end - take) + p.slice(end);
        }
        return p;
    }

    function utf8(u8) {
        if (dec) return dec.decode(u8);
        var s = ''; for (var i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]); return s;
    }

    function done(blob, changed, writes, refused) {
        return { blob: blob, changed: !!changed, writes: writes || [], refused: refused || null };
    }

    /* ---------- JPEG: the XMP APP1 merged, or a new one after the Exif ---------- */

    var XMP_HEAD = 'http://ns.adobe.com/xap/1.0/';

    // Segments from SOI to SOS: [{marker, at, data, end}] (end = first byte after it).
    function jpegHeadSegments(u8) {
        var segs = [], p = 2;
        while (p + 4 <= u8.length) {
            if (u8[p] !== 0xFF) return null;
            var m = u8[p + 1];
            if (m === 0xFF) { p++; continue; }                     // fill byte
            if (m === 0xDA || m === 0xD9) return segs;            // SOS / EOI: the head is over
            if (m >= 0xD0 && m <= 0xD7) { p += 2; continue; }     // RSTn carry no length
            var len = (u8[p + 2] << 8) | u8[p + 3];
            if (len < 2 || p + 2 + len > u8.length) return null;
            segs.push({ marker: m, at: p, data: p + 4, end: p + 2 + len });
            p += 2 + len;
        }
        return null;
    }

    function isXmpApp1(u8, sg) {
        if (sg.marker !== 0xE1 || sg.end - sg.data < XMP_HEAD.length + 1) return false;
        for (var i = 0; i < XMP_HEAD.length; i++) if (u8[sg.data + i] !== XMP_HEAD.charCodeAt(i)) return false;
        return u8[sg.data + XMP_HEAD.length] === 0;
    }

    function isExifApp1(u8, sg) {
        return sg.marker === 0xE1 && u8[sg.data] === 0x45 && u8[sg.data + 1] === 0x78 && u8[sg.data + 2] === 0x69 && u8[sg.data + 3] === 0x66;
    }

    function xmpApp1(packet) {
        var ns = enc.encode(XMP_HEAD), pk = enc.encode(packet);
        var len = 2 + ns.length + 1 + pk.length;
        if (len > 0xFFFF) return null;
        var out = new Uint8Array(2 + len);
        out[0] = 0xFF; out[1] = 0xE1; out[2] = (len >> 8) & 0xff; out[3] = len & 0xff;
        out.set(ns, 4); out[4 + ns.length] = 0; out.set(pk, 5 + ns.length);
        return out;
    }

    function labelJpeg(blob, s) {
        return blob.arrayBuffer().then(function (buf) {
            var u8 = new Uint8Array(buf);
            if (u8.length < 4 || u8[0] !== 0xFF || u8[1] !== 0xD8) return done(blob, false, [], 'not_jpeg');
            var segs = jpegHeadSegments(u8);
            if (!segs) return done(blob, false, [], 'jpeg_unwalkable');
            var old = null, insertAt = 2;
            for (var i = 0; i < segs.length; i++) {
                if (isXmpApp1(u8, segs[i])) { old = segs[i]; break; }
            }
            if (!old) {
                // SOI, APP0 JFIF, APP1 Exif, then XMP — the order exiftool and Adobe write.
                for (var j = 0; j < segs.length; j++) {
                    if (segs[j].marker === 0xE0 || isExifApp1(u8, segs[j])) insertAt = segs[j].end; else break;
                }
            }
            var packet = old ? utf8(u8.subarray(old.data + XMP_HEAD.length + 1, old.end)) : null;
            var merged = xmpMerge(packet != null ? packet : s.baseXmp, s);
            if (!merged) return done(blob, false, [], 'xmp_unreadable');
            var seg = xmpApp1(merged);
            if (!seg) return done(blob, false, [], 'xmp_too_large');
            var parts = old ? [u8.subarray(0, old.at), seg, u8.subarray(old.end)]
                            : [u8.subarray(0, insertAt), seg, u8.subarray(insertAt)];
            return done(new Blob(parts, { type: blob.type || 'image/jpeg' }), true, labelWrites(s, 'XMP (JPEG APP1)'));
        });
    }

    /* ---------- PNG: XMP iTXt merged in place (or after IHDR) + AIGC text chunk ---------- */

    function pngChunk(type, data) {
        var out = new Uint8Array(12 + data.length), dv = new DataView(out.buffer);
        dv.setUint32(0, data.length, false);
        for (var i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
        out.set(data, 8);
        dv.setUint32(8 + data.length, crc32(out.subarray(4, 8 + data.length)), false);
        return out;
    }

    function itxtChunk(keyword, text) {
        var k = enc.encode(keyword), t = enc.encode(text);
        var data = new Uint8Array(k.length + 5 + t.length);   // kw \0 comp(0) method(0) lang \0 tkw \0 text
        data.set(k, 0);
        data.set(t, k.length + 5);
        return pngChunk('iTXt', data);
    }

    function inflate(u8) {
        if (typeof DecompressionStream === 'undefined') return Promise.reject(new Error('no DecompressionStream'));
        var ds = new DecompressionStream('deflate');
        var w = ds.writable.getWriter(); w.write(u8); w.close();
        return new Response(ds.readable).arrayBuffer().then(function (b) { return new Uint8Array(b); });
    }

    // The text of an iTXt chunk (inflated when compressed): Promise<{keyword, text}>.
    function itxtText(data) {
        var z = data.indexOf(0);
        if (z < 0 || z + 3 > data.length) return Promise.resolve(null);
        var keyword = utf8(data.subarray(0, z)), comp = data[z + 1];
        var q = data.indexOf(0, z + 3); if (q < 0) return Promise.resolve(null);   // language tag
        var r = data.indexOf(0, q + 1); if (r < 0) return Promise.resolve(null);   // translated keyword
        var body = data.subarray(r + 1);
        if (!comp) return Promise.resolve({ keyword: keyword, text: utf8(body) });
        return inflate(body).then(function (b) { return { keyword: keyword, text: utf8(b) }; });
    }

    function pngChunks(u8) {
        if (u8.length < 33) return null;
        var sig = [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A];
        for (var i = 0; i < 8; i++) if (u8[i] !== sig[i]) return null;
        var dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength), out = [], p = 8;
        while (p + 12 <= u8.length) {
            var len = dv.getUint32(p, false), type = fourcc(u8, p + 4);
            if (p + 12 + len > u8.length) return null;
            out.push({ type: type, at: p, data: p + 8, len: len, end: p + 12 + len });
            p += 12 + len;
            if (type === 'IEND') break;
        }
        return out.length && out[0].type === 'IHDR' ? out : null;
    }

    function labelPng(blob, s) {
        return blob.arrayBuffer().then(function (buf) {
            var u8 = new Uint8Array(buf), chunks = pngChunks(u8);
            if (!chunks) return done(blob, false, [], 'png_unwalkable');
            var texts = chunks.filter(function (c) { return c.type === 'iTXt' || c.type === 'tEXt' || c.type === 'zTXt'; });
            return Promise.all(texts.map(function (c) {
                var data = u8.subarray(c.data, c.data + c.len);
                if (c.type === 'iTXt') return itxtText(data).catch(function () { return null; });
                var z = data.indexOf(0);
                return Promise.resolve(z > 0 ? { keyword: utf8(data.subarray(0, z)), text: null } : null);
            })).then(function (decoded) {
                var xmpChunk = null, xmpText = null, drop = [];
                texts.forEach(function (c, i) {
                    var d = decoded[i]; if (!d) return;
                    if (d.keyword === 'XML:com.adobe.xmp' && c.type === 'iTXt' && !xmpChunk) { xmpChunk = c; xmpText = d.text; }
                    else if (d.keyword === 'XML:com.adobe.xmp') drop.push(c);          // a second XMP: two answers
                    if (d.keyword === 'AIGC') drop.push(c);                             // one AIGC per file
                });
                var merged = xmpMerge(xmpText != null ? xmpText : s.baseXmp, s);
                if (!merged) return done(blob, false, [], 'xmp_unreadable');
                var ours = [itxtChunk('XML:com.adobe.xmp', merged)];
                if (s.aigc) ours.push(itxtChunk('AIGC', aigcJson(s)));
                var parts = [u8.subarray(0, 8)], placed = false;
                chunks.forEach(function (c) {
                    if (drop.indexOf(c) >= 0) return;
                    if (c === xmpChunk) { ours.forEach(function (o) { parts.push(o); }); placed = true; return; }
                    parts.push(u8.subarray(c.at, c.end));
                    if (c.type === 'IHDR' && !xmpChunk) { ours.forEach(function (o) { parts.push(o); }); placed = true; }
                });
                if (!placed) return done(blob, false, [], 'png_unwalkable');
                var last = chunks[chunks.length - 1];
                if (last.end < u8.length) parts.push(u8.subarray(last.end));            // bytes after IEND stay
                var w = labelWrites(s, 'XMP (PNG iTXt)');
                if (s.aigc) w.push({ where: 'PNG iTXt «AIGC»', field: 'AIGC', value: aigcJson(s) });
                return done(new Blob(parts, { type: blob.type || 'image/png' }), true, w);
            });
        });
    }

    /* ---------- WebP: the XMP chunk merged (simple WebP rebuilt as VP8X) ---------- */

    function labelWebp(blob, s) {
        return blob.arrayBuffer().then(function (buf) {
            var u8 = new Uint8Array(buf);
            if (u8.length < 20) return done(blob, false, [], 'not_webp');
            var dv = new DataView(buf);
            if (dv.getUint32(0, false) !== 0x52494646 || dv.getUint32(8, false) !== 0x57454250) return done(blob, false, [], 'not_webp');
            var chunks = [], p = 12;
            while (p + 8 <= u8.length) {
                var len = dv.getUint32(p + 4, true), end = p + 8 + len + (len & 1);
                if (end > u8.length) { if (p + 8 + len === u8.length) end = u8.length; else return done(blob, false, [], 'webp_unwalkable'); }
                chunks.push({ type: fourcc(u8, p), at: p, data: p + 8, len: len, end: end });
                p = end;
            }
            if (!chunks.length) return done(blob, false, [], 'webp_unwalkable');
            var xmpC = null;
            chunks.forEach(function (c) { if (c.type === 'XMP ' && !xmpC) xmpC = c; });
            var merged = xmpMerge(xmpC ? utf8(u8.subarray(xmpC.data, xmpC.data + xmpC.len)) : s.baseXmp, s);
            if (!merged) return done(blob, false, [], 'xmp_unreadable');
            var xmp = riffChunk('XMP ', enc.encode(merged));
            var parts = [];
            if (chunks[0].type === 'VP8X') {
                if (chunks[0].len < 10) return done(blob, false, [], 'webp_unwalkable');
                var head = u8.slice(chunks[0].at, chunks[0].end);
                head[8] |= 0x04;                                                   // flags: XMP present
                parts.push(head);
                var put = false;
                for (var i = 1; i < chunks.length; i++) {
                    var c = chunks[i];
                    if (c.type === 'XMP ') { if (!put) { parts.push(xmp); put = true; } continue; }
                    parts.push(u8.subarray(c.at, c.end));
                }
                if (!put) parts.push(xmp);                                         // XMP is the last chunk by spec
            } else {
                var dims = webpDims(chunks[0].type, u8.subarray(chunks[0].data, chunks[0].data + chunks[0].len));
                if (!dims || !dims.w || !dims.h) return done(blob, false, [], 'webp_unwalkable');
                var vp8x = new Uint8Array(10);
                vp8x[0] = 0x04 | (dims.alpha ? 0x10 : 0);
                var w = dims.w - 1, h = dims.h - 1;
                vp8x[4] = w & 0xff; vp8x[5] = (w >> 8) & 0xff; vp8x[6] = (w >> 16) & 0xff;
                vp8x[7] = h & 0xff; vp8x[8] = (h >> 8) & 0xff; vp8x[9] = (h >> 16) & 0xff;
                parts.push(riffChunk('VP8X', vp8x));
                chunks.forEach(function (c) { parts.push(u8.subarray(c.at, c.end)); });
                parts.push(xmp);
            }
            var body = concatU8(parts);
            var out = new Uint8Array(12 + body.length);
            out.set(u8.subarray(0, 12), 0); out.set(body, 12);
            new DataView(out.buffer).setUint32(4, out.length - 8, true);
            return done(new Blob([out], { type: blob.type || 'image/webp' }), true, labelWrites(s, 'XMP (WebP chunk)'));
        });
    }

    /* ---------- MP4 / MOV / M4A: XMP in a top-level uuid box at the end ---------- */
    /* Appended after the last box, nothing in the file moves — no chunk offset to
       patch, fragmented files included. An existing XMP box is merged: its type is
       rewritten to 'free' in place (same size) and the merged packet goes to the end,
       so again nothing moves. A last box that "runs to the end of the file" (size 0)
       gets its real size written first. */
    function labelMp4(blob, s) {
        var size = blob.size, boxes = [];
        function walk(off) {
            if (off === size) return Promise.resolve(true);
            if (off + 8 > size || boxes.length > 100000) return Promise.resolve(false);
            return bytesOf(blob, off, Math.min(size, off + 32)).then(function (h) {
                var dv = new DataView(h.buffer, h.byteOffset, h.byteLength);
                var len = dv.getUint32(0, false), head = 8, type = fourcc(h, 4), toEnd = false;
                if (len === 1) { if (h.length < 16) return false; len = dv.getUint32(8, false) * 4294967296 + dv.getUint32(12, false); head = 16; }
                else if (len === 0) { len = size - off; toEnd = true; }
                if (len < head || off + len > size) return false;
                if (boxes.length === 0 && type !== 'ftyp') return false;
                var b = { type: type, at: off, len: len, head: head, toEnd: toEnd, xmp: false };
                if (type === 'uuid' && h.length >= head + 16) {
                    b.xmp = true;
                    for (var i = 0; i < 16; i++) if (h[head + i] !== XMP_UUID[i]) { b.xmp = false; break; }
                }
                boxes.push(b);
                return walk(off + len);
            });
        }
        return walk(0).then(function (ok) {
            if (!ok) return done(blob, false, [], 'mp4_unwalkable');
            return bytesOf(blob, 0, Math.min(size, boxes[0].len, 64)).then(function (ft) {
                var major = fourcc(ft, 8);
                if (/^(heic|heix|hevc|mif1|msf1|avif|avis)$/.test(major)) return done(blob, false, [], 'heif_not_supported');
                var old = null;
                boxes.forEach(function (b) { if (b.xmp && !old) old = b; });
                var read = old && old.len - old.head - 16 <= 4 * 1048576
                    ? bytesOf(blob, old.at + old.head + 16, old.at + old.len).then(utf8)
                    : Promise.resolve(null);
                return read.then(function (packet) {
                    var merged = xmpMerge(packet, s);
                    if (!merged) return done(blob, false, [], 'xmp_unreadable');
                    var pk = enc.encode(merged);
                    var box2 = new Uint8Array(8 + 16 + pk.length);
                    new DataView(box2.buffer).setUint32(0, box2.length, false);
                    box2[4] = 0x75; box2[5] = 0x75; box2[6] = 0x69; box2[7] = 0x64;   // 'uuid'
                    box2.set(XMP_UUID, 8); box2.set(pk, 24);
                    var parts = [], cursor = 0, patches = [];
                    var last = boxes[boxes.length - 1];
                    // Ours from an earlier pass (the last box): replaced, not stacked.
                    if (old && old === last) {
                        return done(new Blob([blob.slice(0, old.at), box2], { type: blob.type || 'video/mp4' }), true, labelWrites(s, 'XMP (MP4 uuid box)'));
                    }
                    if (old) patches.push({ at: old.at + 4, bytes: enc.encode('free') });
                    if (last.toEnd) {
                        if (last.len > 0xFFFFFFFF) return done(blob, false, [], 'mp4_unwalkable');
                        patches.push({ at: last.at, bytes: new Uint8Array(u32be(last.len)) });
                    }
                    patches.sort(function (a, b) { return a.at - b.at; });
                    patches.forEach(function (pt) { parts.push(blob.slice(cursor, pt.at), pt.bytes); cursor = pt.at + pt.bytes.length; });
                    parts.push(blob.slice(cursor), box2);
                    return done(new Blob(parts, { type: blob.type || 'video/mp4' }), true, labelWrites(s, 'XMP (MP4 uuid box)'));
                });
            });
        });
    }

    /* ---------- MP3: frames merged into the existing ID3v2.3/2.4 tag ---------- */
    /* Ours: TXXX digitalsourcetype, TXXX AI_GENERATED, TXXX AIGC (China), and a PRIV
       frame owned by "XMP" with the packet (where ExifTool and Adobe read XMP in an
       MP3). Old frames of those names go; everything else — title, cover, lyrics,
       the person's own comments — is copied byte for byte. Refused (original
       returned): v2.2, a tag with unsynchronisation, an extended header or a
       footer — rewriting those right is a different writer, and a broken tag is
       worse than none. No tag at all → a new v2.3 tag in front. */
    function id3Frame(ver, id, body) {
        var out = new Uint8Array(10 + body.length);
        for (var i = 0; i < 4; i++) out[i] = id.charCodeAt(i);
        out.set(ver === 4 ? syncsafe(body.length) : u32be(body.length), 4);
        out.set(body, 10);
        return out;
    }

    function id3Txxx(ver, desc, value) {
        var body;
        if (/^[\x20-\x7e]*$/.test(desc + value)) {
            var d = enc.encode(desc), v = enc.encode(value);
            body = new Uint8Array(1 + d.length + 1 + v.length);
            body.set(d, 1); body.set(v, 2 + d.length);
        } else if (ver === 4) {
            var d8 = enc.encode(desc), v8 = enc.encode(value);
            body = new Uint8Array(1 + d8.length + 1 + v8.length);
            body[0] = 3; body.set(d8, 1); body.set(v8, 2 + d8.length);
        } else {
            var u16 = function (str) {
                var o = new Uint8Array(2 + str.length * 2); o[0] = 0xFF; o[1] = 0xFE;
                for (var k = 0; k < str.length; k++) { var c = str.charCodeAt(k); o[2 + k * 2] = c & 0xff; o[3 + k * 2] = c >> 8; }
                return o;
            };
            var a = u16(desc), b = u16(value);
            body = new Uint8Array(1 + a.length + 2 + b.length);
            body[0] = 1; body.set(a, 1); body.set(b, 3 + a.length);
        }
        return id3Frame(ver, 'TXXX', body);
    }

    function id3Priv(ver, owner, data) {
        var o = enc.encode(owner), body = new Uint8Array(o.length + 1 + data.length);
        body.set(o, 0); body.set(data, o.length + 1);
        return id3Frame(ver, 'PRIV', body);
    }

    // TXXX description (encodings 0–3) — enough to recognise our own frames.
    function txxxDesc(body) {
        var e = body[0], p = 1;
        if (e === 0 || e === 3) { var z = body.indexOf(0, 1); return utf8(body.subarray(1, z < 0 ? body.length : z)); }
        var s = '';
        if ((body[1] === 0xFF && body[2] === 0xFE) || (body[1] === 0xFE && body[2] === 0xFF)) p = 3;
        var be = body[1] === 0xFE || e === 2;
        for (; p + 1 < body.length; p += 2) {
            var c = be ? (body[p] << 8) | body[p + 1] : body[p] | (body[p + 1] << 8);
            if (!c) break;
            s += String.fromCharCode(c);
        }
        return s;
    }

    var MANAGED_TXXX = { digitalsourcetype: 1, ai_generated: 1, aigc: 1 };

    function mp3Writes(s) {
        var w = [{ where: 'ID3v2 TXXX', field: 'digitalsourcetype', value: s.dst }, { where: 'ID3v2 TXXX', field: 'AI_GENERATED', value: 'true' }];
        if (s.aigc) w.push({ where: 'ID3v2 TXXX', field: 'AIGC', value: aigcJson(s) });
        return w.concat(labelWrites(s, 'XMP (ID3v2 PRIV)'));
    }

    function labelMp3(blob, s) {
        return bytesOf(blob, 0, Math.min(blob.size, 10)).then(function (h) {
            var hasTag = h.length >= 10 && h[0] === 0x49 && h[1] === 0x44 && h[2] === 0x33;
            if (!hasTag) {
                var frames0 = [id3Txxx(3, 'digitalsourcetype', s.dst), id3Txxx(3, 'AI_GENERATED', 'true')];
                if (s.aigc) frames0.push(id3Txxx(3, 'AIGC', aigcJson(s)));
                frames0.push(id3Priv(3, 'XMP', enc.encode(xmpMerge(null, s))));
                var body0 = concatU8(frames0), tag0 = new Uint8Array(10 + body0.length + 256);
                tag0[0] = 0x49; tag0[1] = 0x44; tag0[2] = 0x33; tag0[3] = 3;
                tag0.set(syncsafe(body0.length + 256), 6); tag0.set(body0, 10);
                return done(new Blob([tag0, blob], { type: blob.type || 'audio/mpeg' }), true, mp3Writes(s));
            }
            var ver = h[3], flags = h[5];
            if (ver !== 3 && ver !== 4) return done(blob, false, [], 'id3_version');
            if (flags & 0x80) return done(blob, false, [], 'id3_unsync');
            if (flags & 0x40) return done(blob, false, [], 'id3_ext_header');
            if (ver === 4 && (flags & 0x10)) return done(blob, false, [], 'id3_footer');
            var tagSize = ((h[6] & 0x7F) << 21) | ((h[7] & 0x7F) << 14) | ((h[8] & 0x7F) << 7) | (h[9] & 0x7F);
            if (10 + tagSize > blob.size || tagSize > 64 * 1048576) return done(blob, false, [], 'id3_unwalkable');
            return bytesOf(blob, 10, 10 + tagSize).then(function (t) {
                var keep = [], p = 0, xmpPacket = null;
                while (p + 10 <= t.length && t[p] !== 0) {
                    var id = fourcc(t, p);
                    if (!/^[A-Z0-9]{4}$/.test(id)) return done(blob, false, [], 'id3_unwalkable');
                    var fs = ver === 4
                        ? ((t[p + 4] & 0x7F) << 21) | ((t[p + 5] & 0x7F) << 14) | ((t[p + 6] & 0x7F) << 7) | (t[p + 7] & 0x7F)
                        : ((t[p + 4] << 24) >>> 0) + (t[p + 5] << 16) + (t[p + 6] << 8) + t[p + 7];
                    if (p + 10 + fs > t.length) return done(blob, false, [], 'id3_unwalkable');
                    var fb = t.subarray(p + 10, p + 10 + fs), mine = false;
                    var fflags = (t[p + 8] << 8) | t[p + 9];
                    var plain = ver === 4 ? !(fflags & 0x000F) : !(fflags & 0x00E0);   // no compression/encryption/unsync
                    if (id === 'TXXX' && plain && fs > 1 && MANAGED_TXXX[txxxDesc(fb).toLowerCase()]) mine = true;
                    if (id === 'PRIV' && plain && fs > 4 && fb[0] === 0x58 && fb[1] === 0x4D && fb[2] === 0x50 && fb[3] === 0) {
                        mine = true; if (xmpPacket === null) xmpPacket = utf8(fb.subarray(4));
                    }
                    if (!mine) keep.push(t.subarray(p, p + 10 + fs));
                    p += 10 + fs;
                }
                var merged = xmpMerge(xmpPacket, s);
                if (!merged) return done(blob, false, [], 'xmp_unreadable');
                var ours = [id3Txxx(ver, 'digitalsourcetype', s.dst), id3Txxx(ver, 'AI_GENERATED', 'true')];
                if (s.aigc) ours.push(id3Txxx(ver, 'AIGC', aigcJson(s)));
                ours.push(id3Priv(ver, 'XMP', enc.encode(merged)));
                var frames = concatU8(keep.concat(ours));
                // The old padding absorbs what it can, so a tag with room keeps its size; a
                // tag that has to grow gets padding of its own (exiftool reports a tag that
                // ends on its last frame as «Missing ID3 terminating frame»).
                var newSize = frames.length <= tagSize ? tagSize : frames.length + 256;
                var tag = new Uint8Array(10 + newSize);
                tag[0] = 0x49; tag[1] = 0x44; tag[2] = 0x33; tag[3] = ver; tag[4] = h[4]; tag[5] = flags;
                tag.set(syncsafe(newSize), 6);
                tag.set(frames, 10);                                         // the rest stays zero: padding
                return done(new Blob([tag, blob.slice(10 + tagSize)], { type: blob.type || 'audio/mpeg' }), true, mp3Writes(s));
            });
        });
    }

    /* ---------- WAV: XMP in a _PMX chunk at the end (an old one renamed JUNK) ---------- */
    function labelWav(blob, s) {
        var size = blob.size;
        if (size < 12) return Promise.resolve(done(blob, false, [], 'not_wav'));
        return bytesOf(blob, 0, 12).then(function (h) {
            if (fourcc(h, 0) !== 'RIFF' || fourcc(h, 8) !== 'WAVE') return done(blob, false, [], fourcc(h, 0) === 'RF64' ? 'wav_rf64' : 'not_wav');
            var chunks = [];
            function walk(off) {
                if (off + 8 > size) return Promise.resolve(true);
                if (chunks.length > 10000) return Promise.resolve(false);
                return bytesOf(blob, off, off + 8).then(function (c) {
                    var len = new DataView(c.buffer, c.byteOffset, 8).getUint32(4, true);
                    var t = fourcc(c, 0), body = off + 8 + len;
                    if (!/^[\x20-\x7e]{4}$/.test(t)) return chunks.length > 0;     // trailing bytes: stop here
                    if (body > size) return false;
                    var padded = (len & 1) && body < size;
                    chunks.push({ type: t, at: off, len: len, end: body + (padded ? 1 : 0), needsPad: (len & 1) && body === size });
                    return walk(body + (padded ? 1 : 0));
                });
            }
            return walk(12).then(function (ok) {
                if (!ok || !chunks.length) return done(blob, false, [], 'wav_unwalkable');
                var old = null;
                chunks.forEach(function (c) { if (c.type === '_PMX' && !old) old = c; });
                var read = old && old.len <= 4 * 1048576 ? bytesOf(blob, old.at + 8, old.at + 8 + old.len).then(utf8) : Promise.resolve(null);
                return read.then(function (packet) {
                    var merged = xmpMerge(packet, s);
                    if (!merged) return done(blob, false, [], 'xmp_unreadable');
                    var pmx = riffChunk('_PMX', enc.encode(merged));
                    // Ours from an earlier pass (the last chunk): replaced, not stacked.
                    if (old && old === chunks[chunks.length - 1] && chunks.length > 1) {
                        chunks.pop(); old = null;
                    }
                    var last = chunks[chunks.length - 1], pad = last.needsPad ? new Uint8Array(1) : null;
                    var total = last.end + (pad ? 1 : 0) + pmx.length;
                    if (total - 8 > 0xFFFFFFFF) return done(blob, false, [], 'wav_too_large');
                    var head = new Uint8Array(h.slice(0, 8));
                    new DataView(head.buffer).setUint32(4, total - 8, true);
                    var parts = [head], cursor = 8;
                    if (old) { parts.push(blob.slice(cursor, old.at), enc.encode('JUNK')); cursor = old.at + 4; }
                    parts.push(blob.slice(cursor, last.end));
                    if (pad) parts.push(pad);
                    parts.push(pmx);
                    return done(new Blob(parts, { type: blob.type || 'audio/wav' }), true, labelWrites(s, 'XMP (WAV _PMX chunk)'));
                });
            });
        });
    }

    /* ---------- WebM / MKV: a Tags element (in front of the first Cluster) ---------- */
    function labelWebm(blob, s) {
        var size = blob.size;
        if (size < 8) return Promise.resolve(done(blob, false, [], 'not_webm'));
        return Promise.all([bytesOf(blob, 0, Math.min(size, WEBM_HEAD_BYTES)),
                            bytesOf(blob, Math.max(0, size - 65536), size)]).then(function (r) {
            var head = r[0];
            if (head[0] !== 0x1A || head[1] !== 0x45 || head[2] !== 0xDF || head[3] !== 0xA3) return done(blob, false, [], 'not_webm');
            if (containsAscii(head, 'DIGITAL_SOURCE_TYPE') || containsAscii(r[1], 'DIGITAL_SOURCE_TYPE')) return done(blob, false, [], 'already_labelled');
            var tg = [ebml([0x63, 0xC0], new Uint8Array(0)), simpleTag('AI_GENERATED', 'true'), simpleTag('DIGITAL_SOURCE_TYPE', s.dst)];
            if (s.aiSystem) tg.push(simpleTag('AI_SYSTEM_USED', s.aiSystem));
            if (s.aigc) tg.push(simpleTag('AIGC', aigcJson(s)));
            tg.push(simpleTag('AI_LABEL', s.stamp));
            for (var i = 0; i < tg.length; i++) if (!tg[i]) return done(blob, false, [], 'webm_tag_too_large');
            var tag = ebml([0x73, 0x73], concatU8(tg)), tags = tag && ebml([0x12, 0x54, 0xC3, 0x67], tag);
            if (!tags) return done(blob, false, [], 'webm_tag_too_large');
            return Promise.resolve().then(function () { return webmTagsInFront(blob, head, tags); })
                .catch(function () { return null; })
                .then(function (front) { return front || webmTagsAtEnd(blob, head, tags); })
                .then(function (out) {
                    if (!out || out === blob) return done(blob, false, [], 'webm_unwalkable');
                    var w = [{ where: 'Matroska Tags', field: 'DIGITAL_SOURCE_TYPE', value: s.dst }, { where: 'Matroska Tags', field: 'AI_GENERATED', value: 'true' }];
                    if (s.aiSystem) w.push({ where: 'Matroska Tags', field: 'AI_SYSTEM_USED', value: s.aiSystem });
                    if (s.aigc) w.push({ where: 'Matroska Tags', field: 'AIGC', value: aigcJson(s) });
                    return done(out, true, w);
                });
        });
    }

    function labelAny(blob, filename, spec) {
        var s = labelSpec(spec);
        if (!blob || typeof blob.slice !== 'function') return Promise.resolve(done(blob, false, [], 'no_file'));
        return bytesOf(blob, 0, Math.min(blob.size, 12)).then(function (h) {
            if (h[0] === 0xFF && h[1] === 0xD8) return labelJpeg(blob, s);
            if (h[0] === 0x89 && h[1] === 0x50 && h[2] === 0x4E && h[3] === 0x47) return labelPng(blob, s);
            if (fourcc(h, 0) === 'RIFF' && fourcc(h, 8) === 'WEBP') return labelWebp(blob, s);
            if ((fourcc(h, 0) === 'RIFF' || fourcc(h, 0) === 'RF64') && fourcc(h, 8) === 'WAVE') return labelWav(blob, s);
            if (h.length >= 8 && fourcc(h, 4) === 'ftyp') return labelMp4(blob, s);
            if (h[0] === 0x1A && h[1] === 0x45 && h[2] === 0xDF && h[3] === 0xA3) return labelWebm(blob, s);
            if ((h[0] === 0x49 && h[1] === 0x44 && h[2] === 0x33) || (h[0] === 0xFF && (h[1] & 0xE0) === 0xE0)) return labelMp3(blob, s);
            return done(blob, false, [], 'unsupported');
        }).catch(function (e) {
            return done(blob, false, [], 'error:' + String((e && e.message) || e).slice(0, 80));
        });
    }

    window.__aiMark = {
        mp3: function (blob, tool) { return safe(markMp3, blob, tool); },
        wav: function (blob, tool) { return safe(markWav, blob, tool); },
        png: function (blob, tool) { return safe(markPng, blob, tool); },
        mp4: function (blob, tool) { return safe(markMp4, blob, tool); },
        jpeg: function (blob, tool) { return safe(markJpeg, blob, tool); },
        webm: function (blob, tool) { return safe(markWebm, blob, tool); },
        webp: function (blob, tool) { return safe(markWebp, blob, tool); },
        ogg: function (blob, tool) { return safe(markOgg, blob, tool); },
        // A mark the AUTHOR declares (/ai-label): merges into the person's own
        // metadata, reports what it wrote and where. See the block above labelSpec.
        label: function (blob, filename, spec) {
            try { return labelAny(blob, filename, spec); }
            catch (e) { return Promise.resolve(done(blob, false, [], 'error')); }
        },
        // The XMP packet label() writes (null = a fresh one) — for the page's
        // visible-label lane, which re-encodes a picture and then hands the
        // result back to label(), and for tests.
        xmpMerge: function (packet, spec) { try { return xmpMerge(packet, labelSpec(spec)); } catch (e) { return null; } },
        // Synchronous, on the moov-level udta bytes (null = none): for writers
        // that build the moov themselves. Never throws — a failure keeps `udta`.
        udtaWithMark: function (udta, tool) {
            try { return udtaWithMark(udta, tool); } catch (e) { return udta; }
        },
        // Dispatch by the CONTAINER'S OWN BYTES, falling back to the filename
        // extension only when the magic is unrecognised.
        //
        // It used to dispatch on the extension alone, which turns a merely
        // mislabelled file into a corrupt one: /text-to-speech handed a WAV
        // master to a download named «★-Onyx.mp3», so this wrote an ID3v2 tag
        // in front of RIFF. Android then skips the tag, expects MPEG frames,
        // finds «RIFF» and reports a 0-second file — report
        // ivanveremienko@yandex.ru, 2026-08-04, after 646 tokens spent.
        // Marking must never be the thing that breaks the file it marks, and
        // the bytes are the only honest witness to what the container is.
        auto: function (blob, filename, tool) {
            var self = this;
            var ext = String(filename || '').toLowerCase().split('.').pop();
            var byExt = function () {
                if (ext === 'mp3') return self.mp3(blob, tool);
                if (ext === 'wav') return self.wav(blob, tool);
                if (ext === 'png') return self.png(blob, tool);
                if (ext === 'mp4' || ext === 'm4a' || ext === 'mov') return self.mp4(blob, tool);
                if (ext === 'jpg' || ext === 'jpeg') return self.jpeg(blob, tool);
                if (ext === 'webp') return self.webp(blob, tool);
                if (ext === 'ogg' || ext === 'oga' || ext === 'opus') return self.ogg(blob, tool);
                if (ext === 'webm' || ext === 'mkv') return self.webm(blob, tool);
                return Promise.resolve(blob);
            };
            if (!blob || typeof blob.slice !== 'function') return byExt();
            return blob.slice(0, 12).arrayBuffer().then(function (buf) {
                var h = new Uint8Array(buf);
                // ID3v2 in front of the real container: skip the tag (10-byte
                // header + syncsafe size) and judge what actually follows.
                var off = 0;
                if (h.length >= 10 && h[0] === 0x49 && h[1] === 0x44 && h[2] === 0x33) {
                    return blob.slice(0, 4096).arrayBuffer().then(function (b2) {
                        var a = new Uint8Array(b2);
                        var size = ((a[6] & 0x7F) << 21) | ((a[7] & 0x7F) << 14) | ((a[8] & 0x7F) << 7) | (a[9] & 0x7F);
                        off = 10 + size;
                        if (off + 4 <= a.length) {
                            if (a[off] === 0x52 && a[off + 1] === 0x49 && a[off + 2] === 0x46 && a[off + 3] === 0x46) return self.wav(blob, tool);
                        }
                        return self.mp3(blob, tool);   // ID3 with MPEG frames (or unreadable) — already MP3
                    }, byExt);
                }
                // RIFF is BOTH containers we care about: 'WAVE' at 8 is audio,
                // 'WEBP' at 8 is an image. Before this split every WebP was
                // handed to the WAV writer, which bailed on the missing 'WAVE'
                // and returned it silently unmarked.
                if (h[0] === 0x52 && h[1] === 0x49 && h[2] === 0x46 && h[3] === 0x46) {
                    if (h.length >= 12 && h[8] === 0x57 && h[9] === 0x45 && h[10] === 0x42 && h[11] === 0x50) {
                        return self.webp(blob, tool);
                    }
                    return self.wav(blob, tool);
                }
                if (h[0] === 0x4F && h[1] === 0x67 && h[2] === 0x67 && h[3] === 0x53) return self.ogg(blob, tool);
                if (h[0] === 0xFF && (h[1] & 0xE0) === 0xE0) return self.mp3(blob, tool);
                if (h[0] === 0xFF && h[1] === 0xD8) return self.jpeg(blob, tool);
                if (h[0] === 0x89 && h[1] === 0x50 && h[2] === 0x4E && h[3] === 0x47) return self.png(blob, tool);
                if (h[0] === 0x1A && h[1] === 0x45 && h[2] === 0xDF && h[3] === 0xA3) return self.webm(blob, tool);
                if (h.length >= 8 && h[4] === 0x66 && h[5] === 0x74 && h[6] === 0x79 && h[7] === 0x70) return self.mp4(blob, tool);
                return byExt();
            }, byExt);
        },
    };
})();